/**
 * CommentInfo 类型和 Schema 定义
 *
 * ⚠️ 注意: 此文件由脚本自动生成，请勿手动修改
 * 如需更新，请运行代码生成脚本
 */
import type { SchemaDefinition } from './_schema'
import type { CommentAttachment } from './comment_attachment'
import type { UserBriefInfo } from './user_brief_info'

/**
 * CommentInfo
 */
export interface CommentInfo {
  /** 创建时间（UTC） */
  created_at?: string
  /** 更新时间（UTC） */
  updated_at?: string
  /** 主键（UUID4），全局唯一，不可重复，36位 */
  id?: string
  /** 删除时间（UTC），goframe会自动处理deleted_at，如果想避免goframe自动处理，请使用Unscoped() */
  deleted_at?: string
  /** 删除原因 */
  deleted_reason?: string
  /** 禁用状态 */
  disabled?: boolean
  /** 禁用时间 */
  disabled_at?: string
  /** 禁用截止时间（null表示永久禁用） */
  disabled_until?: string
  /** 禁用原因 */
  disabled_reason?: string
  /** 评论目标类型（post/user/camp/battle） */
  target_type?: string
  /** 评论目标ID */
  target_id?: string
  /** 父评论ID（null表示一级评论），为了提高检索效率 */
  parent_comment_id?: string
  /** 回复的评论ID（null表示一级评论）,和parent_comment_id可能不一样 */
  reply_to_comment_id?: string
  /** 回复的评论作者ID */
  reply_to_user_id?: string
  /** 回复的评论作者信息快照（username、nickname、avatar、bio、段位、等级、星数等） */
  reply_to_user_snapshot?: UserBriefInfo
  /** 评论深度（0=一级评论，1=二级，以此类推） */
  depth?: number
  /** 评论类型 */
  type?: string
  /** 评论文本内容 */
  text?: string
  /** 富媒体附件列表 */
  attachments?: CommentAttachment[]
  /** 附件数量 */
  attachment_count?: number
  /** 发布者ID */
  author_id?: string
  /** 发布者信息快照（username、nickname、avatar、bio、段位、等级、星数等） */
  author_snapshot?: UserBriefInfo
  /** 创建管理员ID（后台评论） */
  creator_staff_id?: string
}

/**
 * CommentInfo Schema
 */
export const CommentInfoSchema: SchemaDefinition = {
  properties: {
    created_at: {
      description: '创建时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '创建时间',
    },
    updated_at: {
      description: '更新时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '更新时间',
    },
    id: {
      description: '主键（UUID4），全局唯一，不可重复，36位',
      format: 'string',
      type: 'string',
      label: '主键',
    },
    deleted_at: {
      description:
        '删除时间（UTC），goframe会自动处理deleted_at，如果想避免goframe自动处理，请使用Unscoped()',
      format: '*time.Time',
      type: 'string',
      label: '删除时间',
    },
    deleted_reason: {
      description: '删除原因',
      format: 'string',
      type: 'string',
      label: '删除原因',
    },
    disabled: {
      description: '禁用状态',
      format: 'bool',
      type: 'boolean',
      label: '禁用状态',
    },
    disabled_at: {
      description: '禁用时间',
      format: '*time.Time',
      type: 'string',
      label: '禁用时间',
    },
    disabled_until: {
      description: '禁用截止时间（null表示永久禁用）',
      format: '*time.Time',
      type: 'string',
      label: '禁用截止时间',
    },
    disabled_reason: {
      description: '禁用原因',
      format: 'string',
      type: 'string',
      label: '禁用原因',
    },
    target_type: {
      description: '评论目标类型（post/user/camp/battle）',
      format: 'cons.CommentTargetType',
      type: 'string',
      label: '评论目标类型',
    },
    target_id: {
      description: '评论目标ID',
      format: 'string',
      type: 'string',
      label: '评论目标ID',
    },
    parent_comment_id: {
      description: '父评论ID（null表示一级评论），为了提高检索效率',
      format: 'string',
      type: 'string',
      label: '父评论ID',
    },
    reply_to_comment_id: {
      description: '回复的评论ID（null表示一级评论）,和parent_comment_id可能不一样',
      format: 'string',
      type: 'string',
      label: '回复的评论ID',
    },
    reply_to_user_id: {
      description: '回复的评论作者ID',
      format: 'string',
      type: 'string',
      label: '回复的评论作者ID',
    },
    reply_to_user_snapshot: {
      $ref: 'UserBriefInfo',
      description: '回复的评论作者信息快照（username、nickname、avatar、bio、段位、等级、星数等）',
      label: '回复的评论作者信息快照',
    },
    depth: {
      description: '评论深度（0=一级评论，1=二级，以此类推）',
      format: 'int',
      type: 'integer',
      label: '评论深度',
    },
    type: {
      description: '评论类型',
      format: 'cons.CommentType',
      type: 'string',
      label: '评论类型',
    },
    text: {
      description: '评论文本内容',
      format: 'string',
      type: 'string',
      label: '评论文本内容',
    },
    attachments: {
      description: '富媒体附件列表',
      format: '[]entity.CommentAttachment',
      items: { $ref: 'CommentAttachment', description: '' },
      type: 'array',
      label: '富媒体附件列表',
    },
    attachment_count: {
      description: '附件数量',
      format: 'int',
      type: 'integer',
      label: '附件数量',
    },
    author_id: {
      description: '发布者ID',
      format: 'string',
      type: 'string',
      label: '发布者ID',
    },
    author_snapshot: {
      $ref: 'UserBriefInfo',
      description: '发布者信息快照（username、nickname、avatar、bio、段位、等级、星数等）',
      label: '发布者信息快照',
    },
    creator_staff_id: {
      description: '创建管理员ID（后台评论）',
      format: 'string',
      type: 'string',
      label: '创建管理员ID',
    },
  },
  type: 'object',
}
